import type { ZodType, ZodTypeDef } from "zod";
import { z } from "zod";
import type { ComponentType, IconRef } from "../tool";
import type { DocumentSetHint } from "./document-set";

// §11/D66 Editor-Framework — SSOT (analog §3.4 Tool-Manifest, Split EditorCore/EditorUI
// wie D16). Ein erweiterbares Editor-System (Blender/UE5-Stil) mit festem 5-Regionen-
// Panel-Layout, registry-getrieben (§3.5-Analogon). „Nichts referenziert einen Editor hart"
// (§1/§5-Analogon) — Querschnitt geht ausschließlich über die EditorRegistry.
//
// Abgrenzung zum Tool: ein Tool ist eine generative, unidirektionale Pipeline (Settings →
// Server-Job → Ergebnis); ein Editor ist eine INTERAKTIVE Frontend-Editing-Fläche über
// einem deklarativen Dokument. D51 bleibt unangetastet: der Editor RECHNET NICHT — TDoc
// ist deklarative Daten, jegliches Rendern/Exportieren läuft server-seitig als Recipe-
// Runner (D45/D51), Persistenz später als Asset/Job (nie Browser→DB, D47/D61).
//
// EditorCore ist React-frei (Backbone-tauglich, wie ToolCore); EditorUI lebt nur im
// Web-Frontend (§11) — strukturell kompatibel mit React.ComponentType, kein React-Dep hier.

/** Die fünf festen Layout-Regionen der EditorShell (§11/D66). */
export const EditorRegion = z.enum(["tools", "toolbar", "viewport", "outliner", "details"]);
export type EditorRegion = z.infer<typeof EditorRegion>;

/** Layout-Hinweis je Region: Default-Größe (px) + ob ein-/ausklappbar. */
export interface EditorLayoutHint {
  /** Default-Größe in px — Breite für tools/outliner/details, Höhe für toolbar. */
  defaultSize?: number;
  /** Ob die Region ein-/ausklappbar ist. Der Viewport ist nie collapsible. */
  collapsible?: boolean;
}

/**
 * §3.4-Analogon (EAGER, deklarativ, React-frei).
 *   TDoc = das PERSISTIERBARE Editor-Dokument (deklarative Daten, Zod-SSOT, D51/D47).
 *   TUi  = der EPHEMERE, editor-eigene UI-State (aktives Werkzeug, Zoom/Pan, Hover) —
 *          NICHT persistiert, NICHT Teil des Dokuments. Genau das macht „anderer Editor =
 *          andere Tools/anderer Viewport" möglich: das Tools-Panel setzt z.B. ui.activeTool,
 *          der Viewport liest es. Über den geteilten EditorContext koppeln sich die Panels.
 */
export interface EditorCore<TDoc = unknown, TUi = unknown> {
  readonly id: string;
  readonly label: string;
  readonly icon: IconRef;
  /** SSOT des persistierbaren Editor-Dokuments. `.parse(unknown) → TDoc` (D12). */
  readonly documentSchema: ZodType<TDoc, ZodTypeDef, unknown>;
  /** Leeres Start-Dokument (muss `documentSchema` genügen). */
  createDocument(): TDoc;
  /** Editor-eigener ephemerer Start-UI-State (z.B. `{ activeTool, zoom, pan }`). Fehlt → `{}`. */
  createUiState?(): TUi;
  /** Optionale Layout-Hinweise je Region; die Shell hat sinnvolle Defaults. */
  readonly layoutHints?: Partial<Record<EditorRegion, EditorLayoutHint>>;
  /**
   * D72 — Multi-Dokument-Host. Fehlt → Single-Document-Editor (Default, bricht keinen
   * bestehenden D66-Editor). Gesetzt → die Shell bietet einen „Levels"-Switcher (Tab) und
   * verwaltet N Dokumente desselben Editor-Typs über die `ProjectSource`. Additive
   * D66-Contract-Erweiterung (Neutralitäts-Prinzip: Mechanismus shared, Substantiv/Verzeichnis
   * editor-spezifisch).
   */
  readonly documentSet?: DocumentSetHint;
}

/**
 * Reicht den GESAMTEN Editor-State an JEDES Panel (analog CapabilityContext §3.2). So
 * koppeln sich editor-eigene Tools ↔ Viewport, ohne dass die Shell die Semantik kennt.
 */
export interface EditorContext<TDoc = unknown, TUi = unknown> {
  doc: TDoc;
  setDoc: (next: TDoc) => void;
  /** ephemerer editor-eigener UI-State (aktives Werkzeug, Zoom/Pan …). */
  ui: TUi;
  setUi: (next: TUi) => void;
  /** id des selektierten Elements (Knoten/Bone/Tile …) oder null. */
  selection: string | null;
  setSelection: (id: string | null) => void;
  /**
   * Shell-Panel-Steuerung — erlaubt der EDITOR-SPEZIFISCHEN Menüleiste (z.B. ein „Ansicht"-/
   * „Fenster"-Menü), die Sichtbarkeit der Regionen abzufragen/zu togglen, ohne dass der Editor
   * den Shell-Store kennt. Von der EditorShell bereitgestellt (optional).
   */
  panels?: {
    isVisible: (region: EditorRegion) => boolean;
    toggle: (region: EditorRegion) => void;
  };
  /** D51-Haken: später Server-Job (Recipe-Runner); im Framework-MVP no-op/disabled. */
  requestExport?: () => void;
}

export interface EditorPanelProps<TDoc = unknown, TUi = unknown> {
  ctx: EditorContext<TDoc, TUi>;
}

/** Ein Editor-Beitrag zu EINER Region; mehrere Panels je Region erlaubt (→ Tabs/Stack). */
export interface PanelDescriptor<TDoc = unknown, TUi = unknown> {
  readonly id: string;
  readonly region: EditorRegion;
  readonly title: string;
  readonly icon?: IconRef;
  /**
   * Die Region-Komponente. Beim Viewport spannt sie selbst `<canvas>`/WebGL/DOM auf — die
   * Shell kennt KEIN Render-Backend (Viewport-Agnostik). Strukturell = React.ComponentType.
   */
  readonly Component: ComponentType<EditorPanelProps<TDoc, TUi>>;
}

/**
 * ToolUI-Analogon (LAZY): lebt nur im Web-Frontend (§11). Flaches `panels[]`; die Shell
 * gruppiert nach `region`. Fehlt ein Panel für eine Region, bleibt der Slot leer.
 */
export interface EditorUI<TDoc = unknown, TUi = unknown> {
  readonly id: string;
  readonly panels: PanelDescriptor<TDoc, TUi>[];
}

/** §3.5-Analogon: Editor-Registry — EditorCore EAGER, EditorUI LAZY (Loader). */
export interface EditorRegistry {
  register(core: EditorCore, ui: () => Promise<{ default: EditorUI }>): void;
  getCore(id: string): EditorCore | undefined;
  loadUI(id: string): Promise<EditorUI | undefined>;
  list(): EditorCore[];
}
